'use client';

import { Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useGetJobDeliverablesQuery,
  usePatchJobDeliverableMutation,
} from '@/features/jobs/api/jobsApi';
import { uiConfig } from '@web/constants';
import {
  GanttChart,
  Badge,
  DatePicker,
  EmptyState,
  MainButton,
  SmartInput,
  Tooltip,
} from '@web/ui';
import { formatDate, getErrorMessage, notify } from '@web/utils';

import styles from './JobTimelineStep.module.scss';
import { TimelineDeliverableEditor } from './TimelineDeliverableEditor';

import type { JobFormMode, JobSummaryDraft } from './types';
import type { JobDeliverable } from '@/features/jobs/types';
import type {
  GanttColumn,
  GanttLinkChange,
  GanttScale,
  GanttTask,
  GanttTaskChange,
} from '@web/ui';
import type { Dayjs } from 'dayjs';

type JobTimelineStepProps = {
  mode: JobFormMode;
  totalSteps: number;
  summary: JobSummaryDraft;
  isSaving: boolean;
  readOnly?: boolean;
  showStepMeta?: boolean;
  showContinue?: boolean;
  onPatchDraft: (
    patch: Partial<JobSummaryDraft>,
    options?: { persist?: boolean },
  ) => Promise<boolean>;
  onContinue: () => void;
};

type DeliverableWithDates = JobDeliverable & {
  start_date?: string | null;
  due_date?: string | null;
  end_date?: string | null;
};

const ZOOM_BY_SCALE: Record<GanttScale, number> = {
  days: 15,
  weeks: 40,
  months: 65,
  year: 90,
};

function toScaleByZoom(zoom: number): GanttScale {
  if (zoom >= 80) return 'year';
  if (zoom >= 55) return 'months';
  if (zoom >= 30) return 'weeks';
  return 'days';
}

function asIsoDate(value?: Dayjs | null) {
  return value ? value.format('YYYY-MM-DD') : null;
}

function resolveDeliverableDateRange(
  deliverable: DeliverableWithDates,
  summaryStartDate?: string | null,
) {
  const fallbackStart = summaryStartDate
    ? dayjs(summaryStartDate).startOf('day')
    : dayjs().startOf('day');

  const start = dayjs(deliverable.start_date ?? fallbackStart);
  const explicitEnd = deliverable.due_date ?? deliverable.end_date ?? null;
  const endFromDuration = start.add(
    Math.max(deliverable.duration ?? 1, 1),
    'day',
  );
  const end = explicitEnd ? dayjs(explicitEnd).startOf('day') : endFromDuration;

  if (!start.isValid()) {
    return { start: fallbackStart, end: fallbackStart };
  }

  if (!end.isValid() || !end.isAfter(start)) {
    return { start, end: start.add(1, 'day') };
  }

  return { start, end };
}

function asDurationLabel(duration: number) {
  if (duration === 1) {
    return '1 day';
  }
  return `${duration} days`;
}

export function JobTimelineStep({
  mode,
  totalSteps,
  summary,
  isSaving,
  readOnly: readOnlyProp,
  showStepMeta = true,
  showContinue = true,
  onPatchDraft,
  onContinue,
}: JobTimelineStepProps) {
  const readOnly = readOnlyProp ?? mode !== 'new';
  const canEdit = !readOnly && summary.status === 'pending';
  const jobId = summary.id ?? '';
  const [scale, setScale] = useState<GanttScale>('weeks');
  const [zoom, setZoom] = useState(40);
  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {},
  );
  const [pendingTaskChanges, setPendingTaskChanges] = useState<
    Record<string, GanttTaskChange>
  >({});

  const {
    data,
    refetch,
    isLoading: isDeliverablesLoadingInitial,
    isFetching: isDeliverablesFetching,
  } = useGetJobDeliverablesQuery(
    { jobId, params: { page_size: 100 } },
    { skip: !summary.id, refetchOnMountOrArgChange: true },
  );
  const [patchDeliverable] = usePatchJobDeliverableMutation();

  useEffect(
    () => () => {
      Object.values(saveTimersRef.current).forEach((timerId) => {
        clearTimeout(timerId);
      });
    },
    [],
  );

  const rows = useMemo(() => {
    const payload = data?.data as
      | { results?: DeliverableWithDates[] }
      | DeliverableWithDates[]
      | null;
    if (!payload) {
      return [];
    }
    const items = Array.isArray(payload) ? payload : (payload.results ?? []);
    return [...items].sort(
      (a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999),
    );
  }, [data?.data]);

  const summaryStartDateIso = asIsoDate(summary.startDate);
  const summaryEndDateIso = asIsoDate(summary.endDate);
  const isDeliverablesLoading =
    Boolean(summary.id) &&
    (isDeliverablesLoadingInitial || isDeliverablesFetching) &&
    rows.length === 0;

  const tasks = useMemo<GanttTask[]>(() => {
    return rows.map((row) => {
      const optimistic = pendingTaskChanges[row.id];
      const { start, end } = optimistic
        ? {
            start: dayjs(optimistic.startDate).startOf('day'),
            end: dayjs(optimistic.endDate).startOf('day'),
          }
        : resolveDeliverableDateRange(row, summaryStartDateIso);
      const durationDays =
        optimistic?.durationDays ?? Math.max(end.diff(start, 'day'), 1);
      return {
        id: row.id,
        label: row.name,
        startDate: start.format('YYYY-MM-DD'),
        endDate: end.format('YYYY-MM-DD'),
        durationDays,
        rowMeta: asDurationLabel(durationDays),
      };
    });
  }, [pendingTaskChanges, rows, summaryStartDateIso]);

  const links = useMemo(
    () =>
      rows
        .filter((row) => Boolean(row.parent_deliverable_id))
        .map((row) => ({
          id: `dependency-${row.id}`,
          sourceTaskId: String(row.parent_deliverable_id),
          targetTaskId: row.id,
          type: 'finish_to_start' as const,
        })),
    [rows],
  );

  const markers = useMemo(() => {
    const result: Array<{
      id: string;
      label: string;
      date: string;
      color: string;
    }> = [];

    if (summaryStartDateIso) {
      result.push({
        id: 'job-start',
        label: 'Start',
        date: summaryStartDateIso,
        color: '#3b82f6',
      });
    }

    if (summaryEndDateIso) {
      result.push({
        id: 'job-finish',
        label: 'Finish',
        date: summaryEndDateIso,
        color: '#10b981',
      });
    }

    return result;
  }, [summaryEndDateIso, summaryStartDateIso]);

  const timelineColumns = useMemo<GanttColumn[]>(
    () => [
      {
        id: 'text',
        header: 'Deliverables',
        width: 90,
        sort: false,
        cell: ({ row }: any) => {
          const label = String(row?.text ?? '');
          return (
            <Tooltip title={label}>
              <span className={styles.jobTimelineStep__deliverableCellLabel}>
                {label}
              </span>
            </Tooltip>
          );
        },
      },
      {
        id: 'start_date',
        header: 'Start date',
        width: 120,
        align: 'center',
        sort: false,
        getter: (row: any) => formatDate(row?.start, uiConfig.dateFormats.date),
      },
      {
        id: 'duration',
        header: 'Duration',
        width: 50,
        align: 'center',
        sort: false,
      },
      {
        id: 'due_date',
        header: 'Due date',
        width: 120,
        align: 'center',
        sort: false,
        getter: (row: any) => formatDate(row?.end, uiConfig.dateFormats.date),
      },
    ],
    [],
  );

  const persistTaskChange = async (change: GanttTaskChange) => {
    if (!canEdit) {
      return;
    }

    const current = rows.find((row) => row.id === change.taskId);
    if (current) {
      const currentRange = resolveDeliverableDateRange(
        current,
        summaryStartDateIso,
      );
      const currentStartDate = currentRange.start.format('YYYY-MM-DD');
      const currentEndDate = currentRange.end.format('YYYY-MM-DD');

      const isUnchanged =
        currentStartDate === change.startDate &&
        currentEndDate === change.endDate;

      if (isUnchanged) {
        return;
      }
    }

    try {
      await patchDeliverable({
        deliverableId: change.taskId,
        payload: {
          start_date: change.startDate,
          due_date: change.endDate,
        },
      }).unwrap();
      await refetch();
      setPendingTaskChanges((current) => {
        const next = { ...current };
        delete next[change.taskId];
        return next;
      });
    } catch (error) {
      setPendingTaskChanges((current) => {
        const next = { ...current };
        delete next[change.taskId];
        return next;
      });
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  const handleTaskChange = (change: GanttTaskChange) => {
    if (!canEdit) {
      return;
    }

    // Reflect edits in the chart immediately.
    setPendingTaskChanges((current) => ({
      ...current,
      [change.taskId]: change,
    }));

    const previousTimer = saveTimersRef.current[change.taskId];
    if (previousTimer) {
      clearTimeout(previousTimer);
    }

    saveTimersRef.current[change.taskId] = setTimeout(() => {
      void persistTaskChange(change);
      delete saveTimersRef.current[change.taskId];
    }, 250);
  };

  const handleLinkChange = async (change: GanttLinkChange) => {
    if (!canEdit) {
      return;
    }

    try {
      if (change.type === 'create') {
        await patchDeliverable({
          deliverableId: change.targetTaskId,
          payload: {
            parent_deliverable_id: change.sourceTaskId,
          },
        }).unwrap();
      } else {
        const linkedRow = links.find((link) => link.id === change.linkId);
        const targetId =
          linkedRow?.targetTaskId ??
          (change.linkId.startsWith('dependency-')
            ? change.linkId.replace('dependency-', '')
            : null);
        if (targetId) {
          await patchDeliverable({
            deliverableId: targetId,
            payload: {
              parent_deliverable_id: null,
            },
          }).unwrap();
        }
      }
      await refetch();
    } catch (error) {
      notify({ type: 'error', message: getErrorMessage(error) });
    }
  };

  return (
    <section className={styles.jobTimelineStep}>
      {showStepMeta ? (
        <div>
          <Badge label={`Step 3 of ${totalSteps}`} tone="default" size="lg" />
        </div>
      ) : null}

      <div className={styles.jobTimelineStep__header}>
        <h2 className={styles.jobTimelineStep__title}>Timeline</h2>
        {showStepMeta ? (
          <p className={styles.jobTimelineStep__subtitle}>
            Set dates and sequencing.
          </p>
        ) : null}
      </div>

      <div className={styles.jobTimelineStep__panel}>
        <div className={styles.jobTimelineStep__controls}>
          <div className={styles.jobTimelineStep__dateControls}>
            <DatePicker
              name="job-timeline-start-date"
              label={`Job Start Date ${!canEdit ? '(Read Only)' : ''}`}
              value={summary.startDate}
              readOnly={!canEdit}
              disabled={!canEdit}
              onChange={(value: Dayjs | null) => {
                if (!canEdit) return;
                void onPatchDraft({ startDate: value }, { persist: true });
              }}
            />
            <DatePicker
              name="job-timeline-end-date"
              label={`Job End Date ${!canEdit ? '(Read Only)' : ''}`}
              value={summary.endDate}
              readOnly={!canEdit}
              disabled={!canEdit}
              onChange={(value: Dayjs | null) => {
                if (!canEdit) return;
                void onPatchDraft({ endDate: value }, { persist: true });
              }}
            />
          </div>
          {summary.status === 'pending' ? (
            <div className={styles.jobTimelineStep__deliverableEditorRow}>
              <TimelineDeliverableEditor
                deliverables={rows}
                summaryStartDateIso={summaryStartDateIso}
                disabled={!canEdit}
                onChange={handleTaskChange}
              />
            </div>
          ) : null}

          <div className={styles.jobTimelineStep__scaleControls}>
            <SmartInput
              type="select"
              name="timeline-scale"
              label="Scale"
              value={scale}
              options={[
                { value: 'days', label: 'Days' },
                { value: 'weeks', label: 'Weeks' },
                { value: 'months', label: 'Months' },
                { value: 'year', label: 'Year' },
              ]}
              onChange={(value) => {
                const nextScale = value as GanttScale;
                setScale(nextScale);
                setZoom(ZOOM_BY_SCALE[nextScale]);
              }}
            />

            <div className={styles.jobTimelineStep__slider}>
              <label htmlFor="timeline-zoom">Zoom</label>
              <input
                id="timeline-zoom"
                type="range"
                min={0}
                max={100}
                step={1}
                value={zoom}
                onChange={(event) => {
                  const nextZoom = Number(event.target.value);
                  setZoom(nextZoom);
                  setScale(toScaleByZoom(nextZoom));
                }}
              />
            </div>
          </div>
        </div>

        {isDeliverablesLoading ? (
          <div data-testid="job-timeline-loading-skeleton">
            <Skeleton variant="rectangular" height={320} />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            title="No deliverables yet."
            description="Add deliverables in Step 2 to build timeline."
          />
        ) : (
          <GanttChart
            tasks={tasks}
            links={links}
            markers={markers}
            columns={timelineColumns}
            scale={scale}
            zoom={zoom}
            readOnly={!canEdit}
            viewportStartDate={summaryStartDateIso ?? undefined}
            viewportEndDate={summaryEndDateIso ?? undefined}
            onTaskChange={handleTaskChange}
            onLinkChange={handleLinkChange}
          />
        )}
      </div>

      {showContinue ? (
        <footer className={styles.jobTimelineStep__footer}>
          <MainButton
            ariaLabel="Continue"
            disabled={isSaving}
            isLoading={isSaving}
            onClick={onContinue}
          >
            Continue
          </MainButton>
        </footer>
      ) : null}
    </section>
  );
}
